import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { CreateOrUpdateDatabaseRequestBodySchema } from 'libs/end-to-end/src/types/databases/create-our-update-database-request-body';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';

export interface DatabasesEditPageHook {
  organizationId: string;
  organizationName: string;
  nameLabel: string;
  name: string;
  setName: Setter<string>;
  descriptionLabel: string;
  description: string;
  setDescription: Setter<string>;
  existingDatabases: Database[];
  submitButtonLabel: string;
  submitButtonBehavior: TouchTargetBehavior;
  submitButtonDisabled: boolean;
  backButtonLabel: string;
  backButtonBehavior: TouchTargetBehavior;
  backButtonDisabled: boolean;
  viewRevisionsButtonLabel: string;
  viewRevisionsButtonBehavior: TouchTargetBehavior;
  viewRevisionsButtonDisabled: boolean;
  saveIndicatorStatus: SaveIndicatorStatus;
}

export const useDatabasesEditPage = (): DatabasesEditPageHook => {
  const { organizationId, databaseId } = useParams();
  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId) {
    throw new Error('organizationId and databaseId are required');
  }

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const patternGetWriteUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [organizationId, databaseId]);

  const patternGetReadUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [organizationId, databaseId]);

  const pattern = useSimpleReadWritePattern({
    writeMethod: 'PUT',
    getReadUrlFn: patternGetReadUrlFn,
    getWriteUrlFn: patternGetWriteUrlFn,
    responseSchema: Database,
    canRead: true,
    canWrite: true,
    writeSchema: CreateOrUpdateDatabaseRequestBodySchema,
  });

  useEffect(() => {
    if (pattern.status === 'done') {
      setName(pattern.entity.name);
      setDescription(pattern.entity.description);

      if (pattern.saved) {
        navigate(
          `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
        );
      }
    }
  }, [databaseId, navigate, organizationId, pattern]);

  return useMemo(() => {
    return {
      organizationId,
      // TODO: Load me from the API.
      name,
      setName,
      description,
      setDescription,
      nameLabel: 'Name',
      descriptionLabel: 'Description',
      organizationName: organizationId,
      existingDatabases: [],
      saveIndicatorStatus: pattern.saveIndicatorStatus,
      submitButtonDisabled:
        pattern.status === 'saving' ||
        pattern.status === 'loading' ||
        name.length <= 0,
      submitButtonLabel: 'Next',
      submitButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (pattern.status !== 'done') {
            return;
          }

          pattern.saveEntity({
            name,
            description,
            organizationId,
          });
        },
      },
      backButtonDisabled:
        pattern.status === 'saving' || pattern.status === 'loading',
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(`/organizations/${organizationId}/databases`);
        },
      },
      viewRevisionsButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/revisions`,
          );
        },
      },
      viewRevisionsButtonDisabled: false,
      viewRevisionsButtonLabel: 'View Revisions',
    };
  }, [databaseId, description, name, navigate, organizationId, pattern]);
};
